"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";

interface RankResult {
  rank: number;
  size: number;
  percentile: number;
  topPercent: number;
}

const BADGE_THRESHOLDS = [5, 10, 25, 50];

function computeRank(rankInput: string, sizeInput: string): RankResult | null {
  const rank = parseIntegerInput(rankInput);
  const size = parseIntegerInput(sizeInput);
  if (rank === null || size === null || rank < 1 || size < 1) {
    return null;
  }
  if (rank > size) {
    return null;
  }
  return {
    rank,
    size,
    percentile: ((size - rank) / size) * 100,
    topPercent: Math.ceil((rank / size) * 100),
  };
}

function computeRequiredRank(
  targetInput: string,
  sizeInput: string
): { target: number; size: number; requiredRank: number } | null {
  const target = parseNumberInput(targetInput);
  const size = parseIntegerInput(sizeInput);
  if (target === null || size === null || size < 1) {
    return null;
  }
  if (target <= 0 || target > 100) {
    return null;
  }
  const requiredRank = Math.max(1, Math.floor((size * target) / 100));
  return { target, size, requiredRank };
}

function earnedBadge(topPercent: number): number | null {
  for (const threshold of BADGE_THRESHOLDS) {
    if (topPercent <= threshold) {
      return threshold;
    }
  }
  return null;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const CLASS_RANK_CALCULATOR_STRINGS = {
  rankPercentile: "Rank → Percentile",
  yourRank: "Your rank",
  classSize: "Class size",
  percentile: "Percentile",
  youAreInThe: "You are in the",
  studentsRankedBelowYou: "Students ranked below you",
  topPercent: "Top {percent}%",
  requiredRankFor: "Required rank for top {percent}%",
  rankOrBetter: "#{rank} or better",
  enterARankOfAt:
    "Enter a rank of at least 1 that is no larger than the class size.",
  targetWhatRankDoI: "Target: What Rank Do I Need?",
  pickATargetTopPercentage:
    "Pick a target top-percentage and your class size to see the lowest rank that still qualifies.",
  targetTop: "Target top %",
  qualifyingStudents: "Qualifying students",
  theFormula: "The Formula",
  percentileClassSizeRankClass:
    "percentile = (class size − rank) ÷ class size × 100",
  topXRankClassSize: "top X% = rank ÷ class size × 100 (rounded up)",
  aPercentileOf9375:
    "A percentile of 93.75 means you performed at or above 93.75% of your class. Note that schools may compute percentile slightly differently — some add 1 to the numerator, handle tied ranks differently, or round in your favor — so check with your registrar for the official number.",
} as const;

export default function ClassRankCalculatorWidget() {
  const [rank, setRank] = useState("");
  const [size, setSize] = useState("");
  const [targetPercent, setTargetPercent] = useState("10");
  const [targetSize, setTargetSize] = useState("");

  const result = computeRank(rank, size);
  const badge = result ? earnedBadge(result.topPercent) : null;
  const reverse = computeRequiredRank(targetPercent, targetSize);

  const strings = useToolUi(CLASS_RANK_CALCULATOR_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Rank to percentile */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.rankPercentile}
        </h2>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="class-rank"
            >
              {strings.yourRank}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="class-rank"
              min={1}
              onChange={(e) => setRank(e.target.value)}
              placeholder="25"
              type="number"
              value={rank}
            />
          </div>
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="class-size"
            >
              {strings.classSize}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="class-size"
              min={1}
              onChange={(e) => setSize(e.target.value)}
              placeholder="400"
              type="number"
              value={size}
            />
          </div>
        </div>
        {result && (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <StatCard
                label={strings.percentile}
                value={fmt(result.percentile, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              />
              <StatCard
                label={strings.youAreInThe}
                value={t(strings.topPercent, {
                  percent: fmt(result.topPercent),
                })}
              />
              <StatCard
                label={strings.studentsRankedBelowYou}
                value={result.size - result.rank}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {BADGE_THRESHOLDS.map((threshold) => (
                <span
                  className={
                    badge !== null && threshold >= badge
                      ? "rounded-full border border-primary bg-primary/10 px-3 py-1 font-medium text-primary text-xs"
                      : "rounded-full border border-border bg-card px-3 py-1 text-muted-foreground text-xs opacity-60"
                  }
                  key={threshold}
                >
                  {t(strings.topPercent, { percent: fmt(threshold) })}
                </span>
              ))}
            </div>
          </>
        )}
        {rank.length > 0 && size.length > 0 && !result && (
          <p className="text-muted-foreground text-sm">
            {strings.enterARankOfAt}
          </p>
        )}
      </section>
      {/* Target mode */}
      <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.targetWhatRankDoI}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.pickATargetTopPercentage}
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="target-percent"
            >
              {strings.targetTop}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="target-percent"
              max={100}
              min={0.1}
              onChange={(e) => setTargetPercent(e.target.value)}
              step="0.1"
              type="number"
              value={targetPercent}
            />
          </div>
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="target-size"
            >
              {strings.classSize}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="target-size"
              min={1}
              onChange={(e) => setTargetSize(e.target.value)}
              placeholder="400"
              type="number"
              value={targetSize}
            />
          </div>
        </div>
        {reverse && (
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              label={t(strings.requiredRankFor, {
                percent: fmt(reverse.target, { maximumFractionDigits: 4 }),
              })}
              value={t(strings.rankOrBetter, {
                rank: fmt(reverse.requiredRank),
              })}
            />
            <StatCard
              label={strings.qualifyingStudents}
              value={reverse.requiredRank}
            />
          </div>
        )}
      </section>
      {/* Formula */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.theFormula}
        </h2>
        <p className="font-mono text-foreground text-sm">
          {strings.percentileClassSizeRankClass}
        </p>
        <p className="font-mono text-foreground text-sm">
          {strings.topXRankClassSize}
        </p>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.aPercentileOf9375}
        </p>
      </section>
    </>
  );
}
